import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * Schalter an/aus (shadcn-Switch-Aufbau): echter `button` mit `role="switch"` und
 * `aria-checked`. Maße nach Entwurf `.tg` (Ticket 1.252): Spur 40 × 22 rund, Knopf 16 weiß mit leichtem Schatten,
 * aus links 3 px, an links 21 px. An = Spur TILL-Dunkelblau `brand` (hell und dunkel wie der aktive Reiter); aus = Spur
 * `checkbox-line` statt Entwurf `#C9CBD0`, weil Bedien-Ränder ≥ 3 : 1 Kontrast brauchen (DESIGN, WCAG 1.4.11).
 * Klickfläche ≥ 24 px hoch, bei Touch ≥ 44 px.
 */
export function Switch({
  checked,
  onCheckedChange,
  className,
  disabled,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'type'> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  // Trefferfläche: der Knopf selbst; die sichtbare Spur liegt darin. Bei Touch mindestens 44 × 44 px, die Spur bleibt 40 × 22 px
  // (DESIGN `spacing.hit-touch`, Story 1.24); mit Maus mindestens 24 px hoch (`min-h-6`), die Spur 22 px liegt mittig darin.
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full min-h-6 disabled:cursor-default disabled:opacity-[var(--opacity-half)] pointer-coarse:h-11 pointer-coarse:min-w-11',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex h-switch-h w-switch-w items-center rounded-full transition-colors',
          checked ? 'bg-brand' : 'bg-checkbox-line',
        )}
      >
        <span
          className={cn(
            'block size-switch-thumb rounded-full bg-on-brand shadow-switch-thumb transition-transform',
            checked ? 'translate-x-switch-on' : 'translate-x-switch-off',
          )}
        />
      </span>
    </button>
  );
}
